import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ITicker,
  IPriceAlert,
  IPriceAlertCreatePayload,
} from '@kattana-io/common';
import Image from '../../../atoms/Image';
import Text from '../../../atoms/Text';
import minus from '../../../assets/images/minus.svg';
import AlertItem from './AlertItem';
import Button from '../../../molecules/Button';
import { toFixed4 } from '../../DexTrades/formatters';
import Switch from '../../../molecules/Switch';
import { useAccount } from 'wagmi';
import { actionCreateNotification } from '../../../actions/notifications';
import {
  actionCancelPriceAlert,
  actionCreatePriceAlert,
} from '../../../actions/price_alerts';
import { IStore } from '../../../reducers';
import useTicker from '../../../hooks/useTicker';
import { numberOnly } from '../../Trading/common/ParamField';
import { getWatchedPriceAlerts } from '../../../selectors/priceAlerts';

import './styles.scss';

interface IPriceAlertCreateProps {
  ticker: ITicker;
  alerts: IPriceAlert[];
  onClose: () => void;
  top: number;
  left: number;
}

const PriceAlertCreate = ({
  ticker,
  alerts,
  top,
  left,
  onClose,
}: IPriceAlertCreateProps) => {
  const dispatch = useDispatch();
  const isCreating = useSelector(
    (state: IStore) => state.price_alerts.creating
  );
  const priceAlertsLimit = useSelector(
    (state: IStore) => state.features.features.priceAlerts
  );
  const watchedPriceAlerts = useSelector(getWatchedPriceAlerts);
  const [isQuoteAlertPrice, setQuoteAlert] = useState(false);
  const oldAlertsAmount = useRef<number>(0);
  const { address: account } = useAccount();

  const inputRef = useRef(null);

  const { quote, base_address, price, price_usd } = useTicker(ticker);

  useEffect(() => {
    oldAlertsAmount.current = alerts.length;
  }, []);

  const onDecline = () => {
    (inputRef.current as any).innerText = isQuoteAlertPrice
      ? toFixed4(price)
      : toFixed4(price_usd);
  };

  const onCreate = () => {
    if (inputRef.current) {
      const { innerText } = inputRef.current;
      const innerTextNumber = Number(innerText);

      // not NaN
      if (innerTextNumber && account) {
        const alreadyExist = alerts.some((el) =>
          isQuoteAlertPrice
            ? el.price === innerTextNumber
            : el.price_usd === innerTextNumber
        );

        if (alreadyExist) {
          dispatch(
            actionCreateNotification({
              kind: 'warning',
              message: 'Current alert has already been set',
            })
          );

          return;
        }

        const alert: IPriceAlertCreatePayload = !isQuoteAlertPrice
          ? {
            token: base_address,
            wallet: account,
            chain: ticker.chain,
            pair: ticker.address,
            price: 0,
            price_usd: innerTextNumber,
            flipped: ticker.flipped,
            gt: innerTextNumber > price_usd,
            name: ticker.name,
            exchange: ticker.exchange,
          }
          : {
            token: base_address,
            wallet: account,
            chain: ticker.chain,
            pair: ticker.address,
            price: innerTextNumber,
            price_usd: 0,
            flipped: ticker.flipped,
            gt: innerTextNumber > price,
            name: ticker.name,
            exchange: ticker.exchange,
          };
        dispatch(actionCreatePriceAlert(alert));

        return;
      }

      // clean value if not a number
      onDecline();
    }
  };

  // if enter, create price alert otherwise only numbers allowed
  const handleKeyDown = (event: any) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onCreate();
    } else {
      numberOnly(event);
    }
  };

  return (
    <>
      <div
        className='price_alert__backdrop'
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      />
      <div
        className='price_alert__create'
        style={{ top, left }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* to prevent from chart reacting to clicks */}
        <div className='row'>
          <div className='price_alert__create__image' />
          <div className='row'>
            <Text size={14} className='text--grey-200'>
              Price alert
            </Text>
            <div
              className='price_alert__create__plus'
              onClick={() => onClose()}
            >
              <Image size='s' src={minus} />
            </div>
          </div>
        </div>
        <div className='price_alert__create__new'>
          <div className='row priceSelector'>
            <div className='quoteSelector'>
              <Switch
                options={{ a: '$', b: quote }}
                onToggle={() => setQuoteAlert((state) => !state)}
                toggle={isQuoteAlertPrice}
                textSize={14}
              />
            </div>
            <div className='input_wrapper'>
              <span className='input text--14'>
                <span
                  suppressContentEditableWarning={true}
                  contentEditable
                  ref={inputRef}
                  onKeyDown={handleKeyDown}
                  style={{ cursor: 'text' }}
                >
                  {toFixed4(isQuoteAlertPrice ? price : price_usd)}
                </span>
              </span>
            </div>
          </div>

          <div className='row'>
            <div className='btnWrapper'>
              <Button
                text='Decline'
                action={onDecline}
                size='s'
                variant='secondary'
              />
            </div>
            <div className='btnWrapper'>
              <Button
                text='Add'
                action={onCreate}
                size='s'
                variant='primary'
                disabled={
                  priceAlertsLimit
                    ? watchedPriceAlerts.length >= priceAlertsLimit
                    : false
                }
              />
            </div>
          </div>
        </div>
        {isCreating && (
          <div className='row'>
            <div className='creating__animation' />
          </div>
        )}
        {alerts.map((alert, index) => (
          <AlertItem
            key={alert.id}
            alert={alert}
            actionConsumePriceAlert={() =>
              dispatch(actionCancelPriceAlert(alert))
            }
            isNew={oldAlertsAmount.current < alerts.length && index === 0}
          />
        ))}
      </div>
    </>
  );
};

export default PriceAlertCreate;
